import React from 'react'
import { Event, EventDetailMetaProps } from '../data/types'
import { CalendarIcon, ClockIcon, EyeIcon, LocationIcon, MapIcon } from '../icons'
import { Button, Card } from './ui'

type Props = {
    event: Event
    meta: EventDetailMetaProps
    formatDate: (dateStr: string) => string
    formatTime: (timeStr: string | null | undefined) => string
    onVolunteerEventClick?: (Event: Event) => void
    canViewEventAllocations?: boolean
}

export const EventHeader = (props: Props) => {
    const { event, meta, formatDate, formatTime, onVolunteerEventClick, canViewEventAllocations = false } = props
    return (
        <Card className="mb-8">
            <div className="relative p-8 bg-repeat bg-tile bg-[url('/public/header_tile_gold.png')]">
                <div className="absolute inset-0 bg-gradient-to-r from-primary-900/50 to-primary-400/30"></div>
                <div className="relative z-10">
                    <h1 className="text-4xl font-bold text-white mb-2">{event.name}</h1>
                    <p className="text-primary-100 text-lg">{event.organizer}</p>
                </div>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center">
                    <CalendarIcon className="w-6 h-6 text-gray-500 dark:text-gray-400 me-3" />
                    <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{meta.dateLabel}</p>
                        <p className="text-gray-900 dark:text-gray-100">{formatDate(event.start_date)}</p>
                    </div>
                </div>
                <div className="flex items-center">
                    <ClockIcon className="w-6 h-6 text-gray-500 dark:text-gray-400 me-3" />
                    <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{meta.timeLabel}</p>
                        <p className="text-gray-900 dark:text-gray-100">
                            {formatTime(event.start_time)} - {formatTime(event.end_time)}
                        </p>
                    </div>
                </div>
                <div className="flex items-center">
                    <LocationIcon className="w-6 h-6 text-gray-500 dark:text-gray-400 me-3" />
                    <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{meta.locationLabel}</p>
                        <p className="text-gray-900 dark:text-gray-100">{event.location}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{event.city}, {event.country}</p>
                    </div>
                </div>
                <div className="flex items-center">
                    <MapIcon className="w-6 h-6 text-gray-500 dark:text-gray-400 me-3" />
                    <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{meta.addressLabel}</p>
                        <p className="text-gray-900 dark:text-gray-100">{event.address}</p>
                    </div>
                </div>

                <hr className="col-span-full my-2 border-t border-gray-400/20 dark:border-gray-200/10" />

                {/* Event Leads */}
                <div className="col-span-full">
                    <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">{meta.leadsTitle || "Event Leads"}:</p>
                    {event.event_leads && event.event_leads.length > 0 ? (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
                            {event.event_leads.map((lead, index) => (
                                <div key={lead.id} className="flex items-center gap-4 text-sm bg-gray-100 dark:bg-gray-900 p-3 rounded-lg">
                                    <div className="flex-1">
                                        <span className="text-gray-900 dark:text-gray-100 font-medium">
                                            {lead.volunteer ? `${lead.volunteer.first_name} ${lead.volunteer.last_name}` : "Unknown"}
                                        </span>
                                        {lead.role && (
                                            <span className="text-gray-600 dark:text-gray-400 ms-2">
                                                ({lead.role})
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-gray-600 dark:text-gray-400">
                                        <span className="font-semibold">{meta.contactTitle}:</span>{" "}
                                        {lead.volunteer?.phone || "Unavailable"}
                                    </div>
                                    {lead.walkie_talkie ? (
                                        <div className="text-gray-600 dark:text-gray-400">
                                            <span className="font-semibold">Walkie Talkie:</span>{" "}
                                            Required
                                        </div>
                                    ) : null}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-gray-500 dark:text-gray-400 text-sm italic">No leads assigned</p>
                    )}
                </div>

                <div className="flex items-center gap-2 text-sm">
                    <p className="text-gray-600 dark:text-gray-400">
                        <span>
                            <span className="font-semibold">{meta.required_volunteer_count_label}:</span>{" "}
                            {event.required_volunteer_count || 0}
                        </span>
                    </p>
                </div>

                <div className="flex items-center gap-2 text-sm">
                    <p className="text-gray-600 dark:text-gray-400">
                        <span>
                            <span className="font-semibold">{meta.allocation_count_label}:</span>{" "}
                            {event.allocation_count || 0}
                            {(event.total_duplications || 0) > 0 && (
                                <span className="text-gray-500 ms-1">(+{event.total_duplications} duplications)</span>
                            )}
                        </span>
                    </p>
                </div>

                <div className="flex items-center gap-2 text-sm">
                    <p className="text-gray-600 dark:text-gray-400">
                        <span>
                            <span className="font-semibold">{meta.multi_allocation_count_label}:</span>{" "}
                            {event.multi_allocation_count || 0}
                        </span>
                    </p>
                </div>

                <div className='text-sm flex gap-4 mt-2 text-gray-600 dark:text-gray-400'>
                    <div>
                        <span className="font-semibold">{meta.accepted_allocations_label}: </span>
                        {event.accepted_count || 0}
                    </div>

                    <div>
                        <span className="font-semibold">{meta.rejected_allocations_label}: </span>
                        {event.rejected_count || 0}
                    </div>

                    <div>
                        <span className="font-semibold">{meta.pending_allocations_label}: </span>
                        {event.pending_count || 0}
                    </div>

                    <div>
                        <span className="font-semibold">{meta.leads_count_label}: </span>
                        {event.leads_count || 0}
                    </div>

                </div>

                {canViewEventAllocations && onVolunteerEventClick && (
                    <div className="col-span-full flex justify-end">
                        <Button
                            type="button"
                            color="primary"
                            onClick={() => onVolunteerEventClick(event)}
                        >
                            <EyeIcon />
                            Show Event Allocations
                        </Button>
                    </div>
                )}
            </div>
        </Card>
    )
}
